/**
 * Which ceremony an XP award earns, decided from the levels the SERVER sent
 * (`XpGained.levelBefore` / `levelAfter`). The client never decides a crossing
 * of its own: it plays the result it was given.
 *
 *   rank   the rank's name changed somewhere in (before, after] — Ten V →
 *          Jack I, Blackjack V → Wild I, Wild V → Wild II · I. Played ONCE,
 *          at the first such level, landing on the final state (an award that
 *          goes Ten IV → Jack II plays one rank-up whose pips light I then II).
 *   level  a level crossed inside one rank — Ten II → III (or II → IV: one
 *          flip, the pips lighting in turn).
 *   null   no level crossed.
 *
 * A straight port of Blockari's `ceremony/plan.ts` over 21 Wild's ladder, and
 * deliberately so: the two games must decide the same ceremony from the same
 * two numbers, or a player who plays both is told a different story by each.
 */

import { rankFor, type Rank } from '@/game/xp';

export type CeremonyKind = 'level' | 'rank';

export interface CeremonyPlan {
  readonly kind: CeremonyKind;
  readonly levelBefore: number;
  readonly levelAfter: number;
  /** The level whose threshold fires the ceremony (the count reaches it). */
  readonly crossLevel: number;
  /** The rank shown until the flip (rank: the old rank's last tier; level: the tier before). */
  readonly out: Rank;
  /** The rank landed on: the final state, always `rankFor(levelAfter)`. */
  readonly in: Rank;
  /** Pips lit before the ceremony and after it (1–5). */
  readonly tierBefore: number;
  readonly tierAfter: number;
}

/** Two levels wear the same rank when the index and the name agree ("Wild" ≠ "Wild II"). */
export function sameRank(a: Rank, b: Rank): boolean {
  return a.index === b.index && a.name === b.name;
}

export function planCeremony(levelBefore: number, levelAfter: number): CeremonyPlan | null {
  const before = Math.max(1, Math.floor(levelBefore));
  const after = Math.floor(levelAfter);
  if (!(after > before)) return null;
  let cross = 0;
  for (let lv = before + 1; lv <= after; lv++) {
    if (!sameRank(rankFor(lv - 1), rankFor(lv))) {
      cross = lv;
      break;
    }
  }
  const final = rankFor(after);
  if (cross > 0) {
    const out = rankFor(cross - 1);
    return {
      kind: 'rank',
      levelBefore: before,
      levelAfter: after,
      crossLevel: cross,
      out,
      in: final,
      tierBefore: out.tier,
      tierAfter: final.tier,
    };
  }
  const start = rankFor(before);
  return {
    kind: 'level',
    levelBefore: before,
    levelAfter: after,
    crossLevel: before + 1,
    out: start,
    in: final,
    tierBefore: start.tier,
    tierAfter: final.tier,
  };
}
